import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Box, Container, Typography, Button, TextField, Alert, CircularProgress,
  Paper, Grid, InputAdornment, IconButton, Tooltip
} from '@mui/material';
import {
  AccessTime as ClockIcon,
  Visibility, VisibilityOff,
  CheckCircle, HelpOutline
} from '@mui/icons-material';
import { signup, getSignupEnabled } from '../api';
import { setToken, setUser } from '../utils/tenantStorage';

export default function SignupPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    company_name: '',
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    confirm_password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // null = still checking; false = invite-only (show message instead of form)
  const [signupEnabled, setSignupEnabled] = useState(null);

  useEffect(() => {
    getSignupEnabled()
      .then(res => setSignupEnabled(!!res.data.enabled))
      .catch(() => setSignupEnabled(false));
  }, []);

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Client-side validation
    if (form.password !== form.confirm_password) {
      setError('Passwords do not match');
      return;
    }
    if (form.password.length < 12) {
      setError('Password must be at least 12 characters');
      return;
    }

    setLoading(true);
    try {
      const res = await signup({
        company_name: form.company_name,
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email,
        password: form.password,
      });

      const { token, user, tenant } = res.data;

      // Store auth and redirect to the new tenant
      setToken(token, tenant.slug);
      setUser(user, tenant.slug);
      navigate(`/${tenant.slug}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f7fa', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ bgcolor: '#1a1a2e', color: 'white', py: 2 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
              onClick={() => navigate('/preview')}
            >
              <ClockIcon sx={{ mr: 1, fontSize: 28 }} />
              <Typography variant="h6" fontWeight="bold">HourTrax</Typography>
            </Box>
            <Button
              variant="outlined"
              size="small"
              sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.5)' }}
              component={Link}
              to="/preview"
            >
              Back to Home
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Invite-only: self-serve signup disabled — route interest to the demo form */}
      {signupEnabled === false && (
        <Container maxWidth="sm" sx={{ flex: 1, display: 'flex', alignItems: 'center', py: 4 }}>
          <Paper elevation={3} sx={{ p: 4, width: '100%', textAlign: 'center' }}>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              HourTrax is currently invite-only
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Self-service signup isn't open yet. Request a demo and we'll get you set up.
            </Typography>
            <Button variant="contained" size="large" onClick={() => navigate('/preview#demo-request')}>
              Request a Demo
            </Button>
          </Paper>
        </Container>
      )}

      {/* Signup Form — only when self-serve signup is enabled */}
      {signupEnabled === true && (
      <Container maxWidth="sm" sx={{ flex: 1, display: 'flex', alignItems: 'center', py: 4 }}>
        <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
          <Typography variant="h4" fontWeight="bold" textAlign="center" gutterBottom>
            Start Your 14-Day Free Trial
          </Typography>
          <Typography variant="body1" color="text.secondary" textAlign="center" sx={{ mb: 3 }}>
            Full access to all features. No credit card required.
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth required name="company_name"
                  label="Company Name"
                  value={form.company_name}
                  onChange={handleChange}
                  placeholder="Acme Staffing LLC"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="Your company name as it appears on invoices and pay stubs">
                          <HelpOutline fontSize="small" color="action" />
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth required name="first_name"
                  label="First Name"
                  value={form.first_name}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth required name="last_name"
                  label="Last Name"
                  value={form.last_name}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth required name="email" type="email"
                  label="Work Email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="you@company.com"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="This will be your login username and the email for account notifications">
                          <HelpOutline fontSize="small" color="action" />
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth required name="password" type={showPassword ? 'text' : 'password'}
                  label="Password"
                  value={form.password}
                  onChange={handleChange}
                  helperText="Minimum 12 characters with uppercase, lowercase, number, and special character"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small">
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth required name="confirm_password" type={showPassword ? 'text' : 'password'}
                  label="Confirm Password"
                  value={form.confirm_password}
                  onChange={handleChange}
                  error={form.confirm_password && form.password !== form.confirm_password}
                  helperText={form.confirm_password && form.password !== form.confirm_password ? 'Passwords do not match' : ''}
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  type="submit" variant="contained" fullWidth size="large"
                  disabled={loading}
                  sx={{ py: 1.5, fontSize: '1rem' }}
                >
                  {loading ? <CircularProgress size={24} /> : 'Create Account'}
                </Button>
              </Grid>
            </Grid>
          </form>

          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Already have an account?{' '}
              <Typography
                component="span" variant="body2" color="primary"
                sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                onClick={() => navigate('/preview')}
              >
                Sign in
              </Typography>
            </Typography>
          </Box>

          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              By creating an account, you agree to our Terms of Service and Privacy Policy.
            </Typography>
          </Box>

          {/* What you get */}
          <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle2" gutterBottom>Your 14-day trial includes:</Typography>
            {[
              'Time tracking & employee management',
              'Automated payroll calculations & pay stubs',
              'Invoice generation & payment pipeline',
              'Custom roles, SSO, and reports',
              'Then $39/mo + $5 per employee',
            ].map((item, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <CheckCircle fontSize="small" color="success" />
                <Typography variant="body2">{item}</Typography>
              </Box>
            ))}
          </Box>
        </Paper>
      </Container>
      )}
    </Box>
  );
}
